import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Truck, Plus, Edit, Trash2, Loader2, Mail, Phone, MapPin, 
  Package, FileText, User, Search, Check, CheckSquare, Square, ArrowRightLeft, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Suppliers = () => {
  const { getAuthHeader } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isBulkEditDialogOpen, setIsBulkEditDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [exportingSupplierId, setExportingSupplierId] = useState(null);

  const exportSupplierCsv = async (supplier) => {
    setExportingSupplierId(supplier.id);
    try {
      const res = await axios.get(`${API_URL}/products/export-csv?supplier_id=${supplier.id}`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_${supplier.name.replace(/[^a-zA-Z0-9 _-]/g, '').trim().replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${supplier.name} exported — edit in Excel (keep the id column), then re-upload via Inventory → Upload Edits`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExportingSupplierId(null);
    }
  };

  // Merge supplier dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeFrom, setMergeFrom] = useState(null); // the supplier being merged
  const [mergeTargetId, setMergeTargetId] = useState(''); // supplier to merge INTO
  const [mergeLoading, setMergeLoading] = useState(false);

  // Good Health pricelist apply state
  const [ghDialogOpen, setGhDialogOpen] = useState(false);
  const [ghPreview, setGhPreview] = useState(null);
  const [ghLoading, setGhLoading] = useState(false);
  const [ghApplying, setGhApplying] = useState(false);
  const [ghSkipDeletes, setGhSkipDeletes] = useState(true);  // safe default: DON'T delete

  // Unassigned-products dialog state
  const [unassignedDialogOpen, setUnassignedDialogOpen] = useState(false);
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const [unassignedSelectedIds, setUnassignedSelectedIds] = useState([]);
  const [unassignedTargetSupplierId, setUnassignedTargetSupplierId] = useState('');
  const [unassignedAssigning, setUnassignedAssigning] = useState(false);

  // Good Health relink dialog state
  const [ghRelinkOpen, setGhRelinkOpen] = useState(false);
  const [ghDiagnose, setGhDiagnose] = useState(null);
  const [ghDiagLoading, setGhDiagLoading] = useState(false);

  const openGoodHealthRelink = async () => {
    setGhRelinkOpen(true);
    setGhDiagnose(null);
    setGhDiagLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/good-health-diagnose`, { headers: getAuthHeader() });
      setGhDiagnose(res.data);
    } catch (err) {
      toast.error('Failed to load: ' + (err?.response?.data?.detail || err.message));
      setGhRelinkOpen(false);
    } finally {
      setGhDiagLoading(false);
    }
  };

  const relinkGoodHealthTo = async (supplierId, supplierName) => {
    if (!window.confirm(`Relink ALL Good Health-branded products to "${supplierName}"? Other "Good …" suppliers will become empty.`)) return;
    try {
      const res = await axios.post(`${API_URL}/admin/good-health-relink-to/${supplierId}`, {}, { headers: getAuthHeader() });
      const d = res.data || {};
      window.alert(
        `RELINK COMPLETE\n\n` +
        `Target supplier        : ${d.target_supplier_name}\n` +
        `Products relinked       : ${d.modified}\n` +
        `Total under this supplier: ${d.final_product_count_under_supplier}\n\n` +
        `Hard-refresh the page to see updated supplier cards.`
      );
      setGhRelinkOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const openGoodHealthDryRun = async () => {
    setGhLoading(true);
    setGhPreview(null);
    setGhDialogOpen(true);
    try {
      const res = await axios.post(`${API_URL}/admin/good-health-upsert-2026may`, {}, { headers: getAuthHeader() });
      setGhPreview(res.data);
    } catch (err) {
      toast.error('Failed: ' + (err?.response?.data?.detail || err.message));
      setGhDialogOpen(false);
    } finally {
      setGhLoading(false);
    }
  };

  const commitGoodHealth = async () => {
    if (!ghPreview) return;
    setGhApplying(true);
    try {
      const url = `${API_URL}/admin/good-health-upsert-2026may?commit=true${ghSkipDeletes ? '&skip_deletes=true' : ''}`;
      const res = await axios.post(url, {}, { headers: getAuthHeader() });
      const r = res.data || {};
      toast.success(`Updated ${r.updated_by_barcode} · Created ${r.created_new} · Deleted ${r.deleted_old}`);
      setGhDialogOpen(false);
      setGhPreview(null);
      fetchData();
    } catch (err) {
      toast.error('Failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setGhApplying(false);
    }
  };

  
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  const [bulkFormData, setBulkFormData] = useState({
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      const [suppliersRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
        // Use 15000 (backend max) so every product is included — production has 9k+.
        // Anything lower silently truncates and Suppliers modal shows missing products.
        axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() })
      ]);
      setSuppliers([...suppliersRes.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', contact_person: '', email: '', phone: '', address: '', notes: '' });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Supplier name is required');
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/suppliers`, formData, { headers: getAuthHeader() });
      toast.success('Supplier added successfully');
      setIsAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add supplier');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/suppliers/${selectedSupplier.id}`, formData, { headers: getAuthHeader() });
      toast.success('Supplier updated successfully');
      setIsEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update supplier');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (supplierId) => {
    const sup = suppliers.find(s => s.id === supplierId);
    const linkedCount = products.filter(p => p.supplier_id === supplierId).length;
    const name = sup?.name || 'this supplier';
    if (linkedCount > 0) {
      const msg =
        `⚠️  WARNING\n\n"${name}" has ${linkedCount} product(s) linked to it.\n\n` +
        `Deleting will UNLINK all those products — they'll become unassigned and you'll have to re-assign them.\n\n` +
        `Recommended: use "Merge into another supplier" instead.\n\n` +
        `Type DELETE to confirm you really want to remove "${name}":`;
      const reply = window.prompt(msg);
      if (!reply || reply.trim().toUpperCase() !== 'DELETE') {
        toast.info('Delete cancelled');
        return;
      }
    } else {
      if (!window.confirm(`Delete supplier "${name}"? (No products are linked.)`)) return;
    }
    try {
      await axios.delete(`${API_URL}/suppliers/${supplierId}`, { headers: getAuthHeader() });
      toast.success(`Supplier "${name}" deleted${linkedCount ? ` — ${linkedCount} products now unassigned` : ''}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const openMergeDialog = (supplier) => {
    setMergeFrom(supplier);
    setMergeTargetId('');
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!mergeFrom || !mergeTargetId) return;
    if (mergeFrom.id === mergeTargetId) {
      toast.error('Pick a different supplier to merge into');
      return;
    }
    const target = suppliers.find(s => s.id === mergeTargetId);
    if (!window.confirm(
      `Merge "${mergeFrom.name}" INTO "${target?.name}"?\n\n` +
      `• All products will move to "${target?.name}".\n` +
      `• "${mergeFrom.name}" will be saved as the BRAND on each product (so reports still group by it).\n` +
      `• "${mergeFrom.name}" will then be deleted.\n\nThis cannot be undone.`
    )) return;
    setMergeLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/suppliers/${mergeFrom.id}/merge-into/${mergeTargetId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'Suppliers merged');
      setMergeDialogOpen(false);
      setMergeFrom(null);
      setMergeTargetId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setMergeLoading(false);
    }
  };

  const openEditDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      notes: supplier.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = async (supplier) => {
    try {
      const response = await axios.get(`${API_URL}/suppliers/${supplier.id}`, { headers: getAuthHeader() });
      setSelectedSupplier(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load supplier details');
    }
  };

  const openAssignDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setSelectedProducts([]);
    setSearchTerm('');
    setIsAssignDialogOpen(true);
  };

  // ── Wholesale pricelist PDF/CSV export ───────────────────────────────────
  // Two-pricing-chain model:
  //   You pay supplier: cost
  //   Buyer (e.g. Health 2000) pays you: cost × (1 + your_margin% / 100)
  //   Buyer's retail to consumers: wholesale × (1 + buyer_markup% / 100)
  // Internal view also compares against your sell_price to flag products
  // where the deal would let the buyer undercut your own shop.
  const [wholesaleSupplier, setWholesaleSupplier] = useState(null);
  const [wholesaleOpen, setWholesaleOpen] = useState(false);
  const [wholesaleMarkup, setWholesaleMarkup] = useState(40);
  const [wholesaleBuyerMarkup, setWholesaleBuyerMarkup] = useState(40);
  const [wholesaleBuyer, setWholesaleBuyer] = useState('Health 2000');
  const [wholesaleSeller, setWholesaleSeller] = useState('The Wellness Store');
  const [wholesaleFormat, setWholesaleFormat] = useState('pdf');
  const [generatingWholesale, setGeneratingWholesale] = useState(false);

  const openWholesaleDialog = (supplier) => {
    setWholesaleSupplier(supplier);
    setWholesaleMarkup(40);
    setWholesaleBuyerMarkup(40);
    setWholesaleBuyer('Health 2000');
    setWholesaleSeller('The Wellness Store');
    setWholesaleFormat('pdf');
    setWholesaleOpen(true);
  };

  const downloadWholesalePricelist = async (view) => {
    if (!wholesaleSupplier) return;
    setGeneratingWholesale(true);
    try {
      const params = new URLSearchParams({
        markup_percent: String(wholesaleMarkup || 0),
        buyer_markup_percent: String(wholesaleBuyerMarkup || 0),
        fmt: wholesaleFormat,
        view,
        buyer_name: wholesaleBuyer || 'Buyer',
        seller_name: wholesaleSeller || '',
      });
      const res = await axios.get(
        `${API_URL}/suppliers/${wholesaleSupplier.id}/wholesale-pricelist?${params.toString()}`,
        { headers: getAuthHeader(), responseType: 'blob', timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: wholesaleFormat === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (wholesaleSupplier.name || 'supplier').replace(/[^a-z0-9]+/gi, '_');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const suffix = view === 'internal' ? '_INTERNAL' : '';
      a.download = `${safe}_wholesale_pricelist${suffix}_${today}.${wholesaleFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${view === 'internal' ? 'Internal' : 'Buyer'} pricelist downloaded.`);
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.response?.data instanceof Blob ? 'Server error — check supplier has products with cost prices' : e.message);
      toast.error('Could not generate pricelist: ' + detail);
    } finally {
      setGeneratingWholesale(false);
    }
  };

  const handleAssignProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.post(
        `${API_URL}/suppliers/${selectedSupplier.id}/assign-products`,
        selectedProducts,
        { headers: getAuthHeader() }
      );
      toast.success(`Assigned ${selectedProducts.length} products to ${selectedSupplier.name}`);
      setIsAssignDialogOpen(false);
      setSelectedProducts([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign products');
    } finally {
      setFormLoading(false);
    }
  };

  // Bulk assign every product matching a brand name (case-insensitive, trimmed)
  // to the currently selected supplier. Includes products that already have a
  // different supplier, so brand wins when staff explicitly confirm.
  const [bulkBrandInput, setBulkBrandInput] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  const handleBulkAssignByBrand = async () => {
    const brand = bulkBrandInput.trim();
    if (!brand) return toast.error('Enter a brand name first');
    if (!selectedSupplier) return;

    // Find every product whose brand matches (trim + case-insensitive)
    const matches = products.filter(p =>
      (p.brand || '').trim().toLowerCase() === brand.toLowerCase()
    );
    if (matches.length === 0) {
      toast.error(`No products found with brand "${brand}"`);
      return;
    }
    if (!window.confirm(
      `Assign ${matches.length} product(s) with brand "${brand}" to supplier "${selectedSupplier.name}"?\n\n` +
      `Any products already linked to a different supplier WILL be re-linked to ${selectedSupplier.name}.`
    )) return;

    setBulkAssigning(true);
    try {
      await axios.post(
        `${API_URL}/suppliers/${selectedSupplier.id}/assign-products`,
        matches.map(p => p.id),
        { headers: getAuthHeader() }
      );
      toast.success(`Assigned ${matches.length} "${brand}" products to ${selectedSupplier.name}`);
      setBulkBrandInput('');
      setIsAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Bulk assign failed');
    } finally {
      setBulkAssigning(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSupplierSelection = (supplierId) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const selectAllSuppliers = () => {
    if (selectedSuppliers.length === suppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(suppliers.map(s => s.id));
    }
  };

  const handleBulkInputChange = (field, value) => {
    setBulkFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBulkEdit = async (e) => {
    e.preventDefault();
    if (selectedSuppliers.length === 0) {
      toast.error('Please select at least one supplier');
      return;
    }

    // Only send non-empty fields
    const updateFields = {};
    if (bulkFormData.contact_person) updateFields.contact_person = bulkFormData.contact_person;
    if (bulkFormData.email) updateFields.email = bulkFormData.email;
    if (bulkFormData.phone) updateFields.phone = bulkFormData.phone;
    if (bulkFormData.address) updateFields.address = bulkFormData.address;
    if (bulkFormData.notes) updateFields.notes = bulkFormData.notes;

    if (Object.keys(updateFields).length === 0) {
      toast.error('Please fill in at least one field to update');
      return;
    }

    setFormLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/suppliers/bulk-update`,
        { supplier_ids: selectedSuppliers, ...updateFields },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      setIsBulkEditDialogOpen(false);
      setBulkFormData({ contact_person: '', email: '', phone: '', address: '', notes: '' });
      setSelectedSuppliers([]);
      setBulkEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update suppliers');
    } finally {
      setFormLoading(false);
    }
  };

  const unassignedProducts = products.filter(p => {
    // Truly unassigned: no supplier_id linked (or empty string)
    if (p.supplier_id && p.supplier_id.toString().trim() !== '') return false;
    const term = (searchTerm || '').toLowerCase().trim();
    if (!term) return true;
    const words = term.split(/\s+/).filter(Boolean);
    const hay = `${p.name || ''} ${p.barcode || ''} ${p.sku || ''} ${p.brand || ''}`.toLowerCase();
    return words.every(w => hay.includes(w));
  }).sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Suppliers
          </h1>
          <p className="text-slate-500 mt-1">Manage your suppliers and assign products</p>
        </div>
        <div className="flex gap-2">
          {bulkEditMode ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => { setBulkEditMode(false); setSelectedSuppliers([]); }}
                data-testid="cancel-bulk-edit"
              >
                Cancel
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setIsBulkEditDialogOpen(true)}
                disabled={selectedSuppliers.length === 0}
                data-testid="open-bulk-edit-dialog"
              >
                <Edit className="w-4 h-4 mr-2" /> Edit {selectedSuppliers.length} Selected
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => setBulkEditMode(true)}
                disabled={suppliers.length === 0}
                data-testid="bulk-edit-mode-button"
              >
                <CheckSquare className="w-4 h-4 mr-2" /> Bulk Edit
              </Button>
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => {
                  setUnassignedSelectedIds([]);
                  setUnassignedSearch('');
                  setUnassignedTargetSupplierId('');
                  setUnassignedDialogOpen(true);
                }}
                data-testid="manage-unassigned-button"
              >
                <Package className="w-4 h-4 mr-2" />
                Unassigned Products ({products.filter(p => !p.supplier_id || p.supplier_id.toString().trim() === '').length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-semibold"
                title="Finds products linked to a non-existent supplier and moves them to 'Unassigned' so you can bulk-assign them"
                data-testid="orphan-fix-button"
                onClick={async () => {
                  if (!window.confirm('Find products whose supplier link is broken and move them to Unassigned?\n\nNo products will be deleted. You can then assign them to the right supplier using the Unassigned Products button.')) return;
                  try {
                    const res = await axios.post(`${API_URL}/admin/orphan-products-to-unassigned`, {}, { headers: getAuthHeader() });
                    const d = res.data || {};
                    if (d.orphans_found === 0) {
                      window.alert('No orphan products found — every product is linked to a real supplier.');
                      return;
                    }
                    const sampleStr = (d.sample || []).slice(0, 10).map(s => `  • ${s.name}`).join('\n');
                    window.alert(
                      `FOUND & FIXED\n\n` +
                      `Orphan products moved to Unassigned: ${d.modified}\n\n` +
                      `Sample:\n${sampleStr}\n\n` +
                      `NEXT STEP:\n` +
                      `Click "Unassigned Products" button → search "Good Health" → Select all → Assign to Good Health NZ`
                    );
                    fetchData();
                  } catch (err) {
                    toast.error('Failed: ' + (err?.response?.data?.detail || err.message));
                  }
                }}
              >
                Find & Fix Orphan Products
              </Button>
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600" onClick={resetForm} data-testid="add-supplier-button">
                    <Plus className="w-4 h-4 mr-2" /> Add Supplier
                  </Button>
                </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">Add Supplier</DialogTitle>
              <DialogDescription>Add a new supplier to manage your product sourcing</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="add-name">Supplier Name *</Label>
                <Input
                  id="add-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g. NZ Supplements Ltd"
                  required
                  data-testid="supplier-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-contact">Contact Person</Label>
                  <Input
                    id="add-contact"
                    value={formData.contact_person}
                    onChange={(e) => handleInputChange('contact_person', e.target.value)}
                    placeholder="John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-phone">Phone</Label>
                  <Input
                    id="add-phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+64 9 123 4567"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-email">Email</Label>
                <Input
                  id="add-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="orders@supplier.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-address">Address</Label>
                <Textarea
                  id="add-address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="123 Business St, Auckland"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-notes">Notes</Label>
                <Textarea
                  id="add-notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Payment terms, delivery notes, etc."
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} className="bg-slate-900" data-testid="submit-supplier">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Supplier'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Bulk Edit Mode Banner */}
      {bulkEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">Bulk Edit Mode</span>
            <span className="text-blue-600">- Click on suppliers to select them</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={selectAllSuppliers}
            className="text-blue-600 hover:text-blue-800"
          >
            {selectedSuppliers.length === suppliers.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      )}

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.length === 0 ? (
          <Card className="col-span-full border rounded-sm">
            <CardContent className="text-center py-12 text-slate-500">
              <Truck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No suppliers yet</p>
              <p className="text-sm mt-1">Add your first supplier to start organizing products</p>
            </CardContent>
          </Card>
        ) : (
          suppliers.map((supplier) => (
            <Card 
              key={supplier.id} 
              className={`border rounded-sm hover:shadow-md transition-all cursor-pointer ${
                bulkEditMode && selectedSuppliers.includes(supplier.id) 
                  ? 'ring-2 ring-blue-500 bg-blue-50' 
                  : ''
              }`}
              onClick={() => bulkEditMode ? toggleSupplierSelection(supplier.id) : openViewDialog(supplier)}
              data-testid={`supplier-card-${supplier.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {bulkEditMode ? (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedSuppliers.includes(supplier.id) ? 'bg-blue-500' : 'bg-slate-100'
                      }`}>
                        {selectedSuppliers.includes(supplier.id) ? (
                          <Check className="w-5 h-5 text-white" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <Truck className="w-5 h-5 text-orange-600" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{supplier.name}</CardTitle>
                      {supplier.contact_person && (
                        <p className="text-sm text-slate-500">{supplier.contact_person}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {supplier.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{supplier.email}</span>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                </div>
                
                <Separator className="my-3" />
                
                <div className="flex items-center justify-between">
                  <div className="flex gap-4">
                    <Badge variant="outline" className="bg-blue-50">
                      <Package className="w-3 h-3 mr-1" />
                      {supplier.product_count} Products
                    </Badge>
                    <Badge variant="outline" className="bg-emerald-50" title="Value of stock on hand from this supplier (qty × cost)">
                      <Package className="w-3 h-3 mr-1" />
                      Stock ${(supplier.catalogue_value || 0).toLocaleString()}
                    </Badge>
                    {supplier.total_invoices > 0 && (
                      <Badge variant="outline" className="bg-amber-50" title="Total of recorded invoices from this supplier">
                        <FileText className="w-3 h-3 mr-1" />
                        Invoiced ${supplier.total_invoices.toLocaleString()}
                      </Badge>
                    )}
                  </div>
                </div>
                
                {!bulkEditMode && (
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openAssignDialog(supplier)}
                    data-testid={`assign-products-${supplier.id}`}
                  >
                    <Package className="w-4 h-4 mr-1" /> Assign
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openMergeDialog(supplier)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    title="Merge this supplier into another (e.g. merge a brand-name supplier into the real distributor)"
                    data-testid={`merge-supplier-${supplier.id}`}
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openWholesaleDialog(supplier)}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    title="Generate a wholesale-pricelist PDF (with your markup) to email a buyer like Health 2000"
                    data-testid={`wholesale-pricelist-${supplier.id}`}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={exportingSupplierId === supplier.id}
                    onClick={() => exportSupplierCsv(supplier)}
                    className="border-sky-300 text-sky-700 hover:bg-sky-50"
                    title="Export this supplier's products to CSV — edit names/descriptions/prices in Excel, then re-upload via Inventory → Upload Edits"
                    data-testid={`export-supplier-csv-${supplier.id}`}
                  >
                    {exportingSupplierId === supplier.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openEditDialog(supplier)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(supplier.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditDialogOpen} onOpenChange={setIsBulkEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Bulk Edit {selectedSuppliers.length} Suppliers
            </DialogTitle>
            <DialogDescription>
              Update common fields for all selected suppliers. Leave fields empty to keep existing values.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bulk-contact">Contact Person</Label>
                <Input
                  id="bulk-contact"
                  value={bulkFormData.contact_person}
                  onChange={(e) => handleBulkInputChange('contact_person', e.target.value)}
                  placeholder="Leave empty to keep existing"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulk-phone">Phone</Label>
                <Input
                  id="bulk-phone"
                  value={bulkFormData.phone}
                  onChange={(e) => handleBulkInputChange('phone', e.target.value)}
                  placeholder="Leave empty to keep existing"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-email">Email</Label>
              <Input
                id="bulk-email"
                type="email"
                value={bulkFormData.email}
                onChange={(e) => handleBulkInputChange('email', e.target.value)}
                placeholder="Leave empty to keep existing"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-address">Address</Label>
              <Textarea
                id="bulk-address"
                value={bulkFormData.address}
                onChange={(e) => handleBulkInputChange('address', e.target.value)}
                placeholder="Leave empty to keep existing"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-notes">Notes</Label>
              <Textarea
                id="bulk-notes"
                value={bulkFormData.notes}
                onChange={(e) => handleBulkInputChange('notes', e.target.value)}
                placeholder="Leave empty to keep existing"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsBulkEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-bulk-edit">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Update ${selectedSuppliers.length} Suppliers`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Edit Supplier</DialogTitle>
            <DialogDescription>Update supplier information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Supplier Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g. NZ Supplements Ltd"
                required
                data-testid="edit-supplier-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-contact">Contact Person</Label>
                <Input
                  id="edit-contact"
                  value={formData.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+64 9 123 4567"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="orders@supplier.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Textarea
                id="edit-address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="123 Business St, Auckland"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Payment terms, delivery notes, etc."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading} className="bg-slate-900" data-testid="update-supplier">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0">
          {selectedSupplier && (
            <>
              {/* Supplier Header */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-8 py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Truck className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold tracking-tight" data-testid="supplier-detail-name">
                        {selectedSupplier.name}
                      </h2>
                      <p className="text-slate-300 text-sm mt-1">
                        {selectedSupplier.products?.length || 0} products in catalogue
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/10 hover:bg-white/20 text-white border-0"
                      onClick={(e) => { e.stopPropagation(); openAssignDialog(selectedSupplier); }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Assign Products
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/10 hover:bg-white/20 text-white border-0"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setIsViewDialogOpen(false);
                        setFormData({
                          name: selectedSupplier.name,
                          contact_person: selectedSupplier.contact_person || '',
                          email: selectedSupplier.email || '',
                          phone: selectedSupplier.phone || '',
                          address: selectedSupplier.address || '',
                          notes: selectedSupplier.notes || ''
                        });
                        setIsEditDialogOpen(true);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </div>
                </div>

                {/* Contact Details Row */}
                <div className="flex flex-wrap gap-6 mt-5">
                  {selectedSupplier.contact_person && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <User className="w-4 h-4 text-orange-400" />
                      <span className="text-sm">{selectedSupplier.contact_person}</span>
                    </div>
                  )}
                  {selectedSupplier.email && (
                    <a href={`mailto:${selectedSupplier.email}`} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                      <Mail className="w-4 h-4 text-orange-400" />
                      <span className="text-sm">{selectedSupplier.email}</span>
                    </a>
                  )}
                  {selectedSupplier.phone && (
                    <a href={`tel:${selectedSupplier.phone}`} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                      <Phone className="w-4 h-4 text-orange-400" />
                      <span className="text-sm">{selectedSupplier.phone}</span>
                    </a>
                  )}
                  {selectedSupplier.address && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <MapPin className="w-4 h-4 text-orange-400" />
                      <span className="text-sm">{selectedSupplier.address}</span>
                    </div>
                  )}
                </div>

                {selectedSupplier.notes && (
                  <p className="mt-3 text-sm text-slate-400 bg-white/5 px-4 py-2 rounded-lg">
                    {selectedSupplier.notes}
                  </p>
                )}
              </div>

              {/* Product Stats Bar */}
              <div className="px-8 py-3 bg-slate-50 border-b flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">
                    {selectedSupplier.products?.length || 0} Products
                  </span>
                </div>
                {selectedSupplier.products?.length > 0 && (
                  <>
                    <Separator orientation="vertical" className="h-5" />
                    <span className="text-sm text-slate-500">
                      Total Stock: <strong>{selectedSupplier.products.reduce((sum, p) => sum + (p.quantity || 0), 0)}</strong> units
                    </span>
                    <Separator orientation="vertical" className="h-5" />
                    <span className="text-sm text-slate-500">
                      Catalogue Value: <strong>${selectedSupplier.products.reduce((sum, p) => sum + ((p.buy_price || 0) * (p.quantity || 0)), 0).toFixed(2)}</strong>
                    </span>
                  </>
                )}
              </div>

              {/* Products Table */}
              <div className="flex-1 overflow-auto">
                {selectedSupplier.products?.length > 0 ? (
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow className="border-b-2">
                        <TableHead className="pl-8 text-xs font-bold uppercase tracking-wider text-slate-500 w-12">#</TableHead>
                        <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Product Name</TableHead>
                        <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">SKU</TableHead>
                        <TableHead className="text-center text-xs font-bold uppercase tracking-wider text-slate-500">Stock</TableHead>
                        <TableHead className="text-right text-xs font-bold uppercase tracking-wider text-slate-500">Buy Price</TableHead>
                        <TableHead className="text-right text-xs font-bold uppercase tracking-wider text-slate-500">Sell Price</TableHead>
                        <TableHead className="text-right pr-8 text-xs font-bold uppercase tracking-wider text-slate-500">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...selectedSupplier.products].sort((a, b) => a.name.localeCompare(b.name)).map((p, idx) => {
                        // sell_price is inc-GST, buy_price ex-GST — strip GST off sell first
                        const sellExGst = p.sell_price > 0 ? p.sell_price / 1.15 : 0;
                        const margin = sellExGst > 0 ? ((sellExGst - p.buy_price) / sellExGst * 100) : 0;
                        const isLowStock = p.quantity <= 2;
                        return (
                          <TableRow key={p.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-orange-50/50 transition-colors`}>
                            <TableCell className="pl-8 text-slate-400 text-sm">{idx + 1}</TableCell>
                            <TableCell>
                              <span className="font-medium text-slate-900">{p.name}</span>
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm font-mono">{p.sku || '—'}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={isLowStock ? "destructive" : "secondary"} className={`text-xs font-semibold ${!isLowStock ? 'bg-emerald-100 text-emerald-700' : ''}`}>
                                {p.quantity}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-slate-700">${(p.buy_price || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right text-sm font-medium text-slate-900">${(p.sell_price || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right pr-8">
                              <span className={`text-sm font-semibold ${margin >= 50 ? 'text-emerald-600' : margin >= 35 ? 'text-amber-600' : 'text-red-500'}`}>
                                {margin.toFixed(0)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-16 text-slate-400">
                    <Package className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                    <p className="text-lg font-medium">No products assigned yet</p>
                    <p className="text-sm mt-1">Click "Assign Products" to add products to this supplier</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Good Health Pricelist Apply Dialog */}
      <Dialog open={ghDialogOpen} onOpenChange={setGhDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Apply Good Health 2026 May Pricelist
            </DialogTitle>
            <DialogDescription>
              Review what will change before committing. Stock counts are preserved on updates.
            </DialogDescription>
          </DialogHeader>

          {ghLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : ghPreview && (
            <div className="flex flex-col flex-1 min-h-0 gap-3">
              {/* Counts */}
              <div className="grid grid-cols-3 gap-3 shrink-0">
                <Card className="p-3 border-emerald-200 bg-emerald-50">
                  <p className="text-xs text-emerald-700 uppercase">Update</p>
                  <p className="text-2xl font-bold text-emerald-700">{ghPreview.updated_by_barcode}</p>
                  <p className="text-[10px] text-slate-500">Stock preserved</p>
                </Card>
                <Card className="p-3 border-blue-200 bg-blue-50">
                  <p className="text-xs text-blue-700 uppercase">Create New</p>
                  <p className="text-2xl font-bold text-blue-700">{ghPreview.created_new}</p>
                  <p className="text-[10px] text-slate-500">qty 0 to start</p>
                </Card>
                <Card className="p-3 border-red-200 bg-red-50">
                  <p className="text-xs text-red-700 uppercase">Delete Old</p>
                  <p className="text-2xl font-bold text-red-700">{ghPreview.deleted_old}</p>
                  <p className="text-[10px] text-slate-500">Not on new pricelist</p>
                </Card>
              </div>

              {/* Delete safety toggle */}
              {ghPreview.deleted_old > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded p-3 shrink-0">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ghSkipDeletes}
                      onChange={(e) => setGhSkipDeletes(e.target.checked)}
                      className="mt-1"
                      data-testid="gh-skip-deletes-checkbox"
                    />
                    <div>
                      <p className="font-semibold text-amber-900">Skip deletes — only UPDATE and CREATE</p>
                      <p className="text-xs text-amber-700">
                        Leaves the {ghPreview.deleted_old} obsolete SKUs in inventory (with their existing stock). Recommended on first run.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* The delete list */}
              {ghPreview.deleted_old > 0 && (
                <div className="flex flex-col flex-1 min-h-0 gap-2">
                  <p className="text-sm font-semibold text-slate-700 shrink-0">
                    These {ghPreview.deleted_old} products are currently linked to GOOD HEALTH NZ but are <strong>not on the new pricelist</strong>:
                  </p>
                  <div className="flex-1 min-h-0 overflow-y-auto border rounded bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-left">
                          <th className="px-3 py-2 w-8">#</th>
                          <th className="px-3 py-2">Product Name</th>
                          <th className="px-3 py-2 w-36">Barcode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ghPreview.full_delete_list || []).map((d, i) => (
                          <tr key={d.id || i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                            <td className="px-3 py-1.5">{d.name}</td>
                            <td className="px-3 py-1.5 font-mono text-slate-500">{d.barcode || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => { setGhDialogOpen(false); setGhPreview(null); }}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={ghLoading || ghApplying || !ghPreview}
              onClick={commitGoodHealth}
              data-testid="gh-commit-btn"
            >
              {ghApplying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {ghSkipDeletes && ghPreview?.deleted_old > 0
                ? `Apply (update+create only, skip ${ghPreview.deleted_old} deletes)`
                : `Apply changes`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Unassigned Products (cross-supplier) Dialog */}
      <Dialog open={unassignedDialogOpen} onOpenChange={setUnassignedDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Unassigned Products
            </DialogTitle>
            <DialogDescription>
              Products with no supplier set. Pick them, choose a supplier, hit Assign.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const allUnassigned = products
              .filter(p => !p.supplier_id || p.supplier_id.toString().trim() === '')
              .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
            const term = (unassignedSearch || '').toLowerCase().trim();
            const words = term.split(/\s+/).filter(Boolean);
            const visible = !term ? allUnassigned : allUnassigned.filter(p => {
              const hay = `${p.name || ''} ${p.brand || ''} ${p.barcode || ''} ${p.sku || ''}`.toLowerCase();
              return words.every(w => hay.includes(w));
            });
            const allVisibleSelected = visible.length > 0 && visible.every(p => unassignedSelectedIds.includes(p.id));
            return (
              <div className="space-y-4">
                {/* Supplier picker */}
                <div className="space-y-2">
                  <Label>Assign selected to supplier *</Label>
                  <Select
                    value={unassignedTargetSupplierId}
                    onValueChange={setUnassignedTargetSupplierId}
                  >
                    <SelectTrigger data-testid="unassigned-target-supplier">
                      <SelectValue placeholder="Choose a supplier..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...suppliers].sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name / brand / barcode..."
                    value={unassignedSearch}
                    onChange={(e) => setUnassignedSearch(e.target.value)}
                    className="pl-9"
                    data-testid="unassigned-search"
                  />
                </div>

                {/* Select all bar */}
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs text-slate-500">
                    {visible.length} of {allUnassigned.length} unassigned shown • {unassignedSelectedIds.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={visible.length === 0}
                    data-testid="unassigned-select-all-btn"
                    onClick={() => {
                      const ids = visible.map(p => p.id);
                      if (allVisibleSelected) {
                        setUnassignedSelectedIds(prev => prev.filter(id => !ids.includes(id)));
                      } else {
                        setUnassignedSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
                      }
                    }}
                  >
                    {allVisibleSelected ? 'Deselect all' : `Select all (${visible.length})`}
                  </Button>
                </div>

                {/* List */}
                <ScrollArea className="h-[340px] border rounded-sm">
                  {visible.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>{allUnassigned.length === 0 ? 'All products have suppliers — nothing to assign.' : 'No products match your search.'}</p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {visible.slice(0, 500).map((product) => {
                        const selected = unassignedSelectedIds.includes(product.id);
                        return (
                          <button
                            key={product.id}
                            onClick={() => {
                              setUnassignedSelectedIds(prev =>
                                prev.includes(product.id)
                                  ? prev.filter(id => id !== product.id)
                                  : [...prev, product.id]
                              );
                            }}
                            className={`w-full flex items-center justify-between p-3 rounded-sm transition-colors text-left ${
                              selected ? 'bg-orange-100 border border-orange-300' : 'bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            <div>
                              <p className="font-medium">{product.name}</p>
                              <p className="text-xs text-slate-500">
                                {product.brand ? `${product.brand} • ` : ''}
                                {product.barcode || 'No barcode'} • Stock: {product.quantity ?? 0} • ${(product.sell_price ?? 0).toFixed(2)}
                              </p>
                            </div>
                            {selected && <Check className="w-5 h-5 text-orange-600 shrink-0" />}
                          </button>
                        );
                      })}
                      {visible.length > 500 && (
                        <p className="text-center text-xs text-slate-500 py-2">
                          Showing first 500 of {visible.length} — narrow your search to see more.
                        </p>
                      )}
                    </div>
                  )}
                </ScrollArea>

                {/* Footer */}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-sm text-slate-600">
                    {unassignedSelectedIds.length} product(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setUnassignedDialogOpen(false)}>
                      Close
                    </Button>
                    <Button
                      className="bg-orange-500 hover:bg-orange-600"
                      disabled={
                        unassignedAssigning ||
                        unassignedSelectedIds.length === 0 ||
                        !unassignedTargetSupplierId
                      }
                      data-testid="unassigned-assign-btn"
                      onClick={async () => {
                        const sup = suppliers.find(s => s.id === unassignedTargetSupplierId);
                        if (!sup) return;
                        if (!window.confirm(`Assign ${unassignedSelectedIds.length} product(s) to "${sup.name}"?`)) return;
                        setUnassignedAssigning(true);
                        try {
                          await axios.post(
                            `${API_URL}/suppliers/${unassignedTargetSupplierId}/assign-products`,
                            unassignedSelectedIds,
                            { headers: getAuthHeader() }
                          );
                          toast.success(`Assigned ${unassignedSelectedIds.length} products to ${sup.name}`);
                          setUnassignedSelectedIds([]);
                          setUnassignedDialogOpen(false);
                          fetchData();
                        } catch (err) {
                          toast.error('Assign failed: ' + (err?.response?.data?.detail || err.message));
                        } finally {
                          setUnassignedAssigning(false);
                        }
                      }}
                    >
                      {unassignedAssigning ? <Loader2 className="w-4 h-4 animate-spin" /> : `Assign ${unassignedSelectedIds.length || ''} to supplier`}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>


      {/* Assign Products Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Assign Products to {selectedSupplier?.name}
            </DialogTitle>
            <DialogDescription>
              Select products to assign to this supplier
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Bulk-assign by brand — one-click cleanup for cases like "Solgar → The Bountiful Company" */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                Bulk-assign every product with a specific brand to <strong>{selectedSupplier?.name}</strong>
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. Solgar, Nuzest..."
                  value={bulkBrandInput}
                  onChange={(e) => setBulkBrandInput(e.target.value)}
                  className="flex-1"
                  data-testid="bulk-assign-brand-input"
                />
                <Button
                  onClick={handleBulkAssignByBrand}
                  disabled={bulkAssigning || !bulkBrandInput.trim()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="bulk-assign-brand-btn"
                >
                  {bulkAssigning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign by brand'}
                </Button>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Matches every product whose <strong>Brand</strong> equals this name (case-insensitive). Including products already linked to a different supplier.
              </p>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search unassigned products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Select All / Deselect All */}
            {unassignedProducts.length > 0 && (
              <div className="flex items-center justify-between px-1">
                <span className="text-xs text-slate-500">
                  {unassignedProducts.length} unassigned product{unassignedProducts.length === 1 ? '' : 's'} {searchTerm ? 'matching' : 'available'}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="select-all-unassigned-btn"
                  onClick={() => {
                    const allIds = unassignedProducts.map(p => p.id);
                    const allSelected = allIds.length > 0 && allIds.every(id => selectedProducts.includes(id));
                    if (allSelected) {
                      // Deselect just the currently visible ones
                      setSelectedProducts(prev => prev.filter(id => !allIds.includes(id)));
                    } else {
                      // Select all visible (merge with anything already selected)
                      setSelectedProducts(prev => Array.from(new Set([...prev, ...allIds])));
                    }
                  }}
                >
                  {unassignedProducts.length > 0 && unassignedProducts.every(p => selectedProducts.includes(p.id))
                    ? 'Deselect all'
                    : `Select all (${unassignedProducts.length})`}
                </Button>
              </div>
            )}
            
            <ScrollArea className="h-[300px] border rounded-sm">
              {unassignedProducts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No unassigned products</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {unassignedProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-sm transition-colors ${
                        selectedProducts.includes(product.id)
                          ? 'bg-orange-100 border border-orange-300'
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                    >
                      <div className="text-left">
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-slate-500">
                          Stock: {product.quantity} | ${product.sell_price.toFixed(2)}
                        </p>
                      </div>
                      {selectedProducts.includes(product.id) && (
                        <Check className="w-5 h-5 text-orange-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">
                {selectedProducts.length} product(s) selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAssignProducts} 
                  disabled={formLoading || selectedProducts.length === 0}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign Products'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Supplier Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="merge-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" />
              Merge Supplier
            </DialogTitle>
            <DialogDescription>
              Move all products from <strong>{mergeFrom?.name || ''}</strong> into another supplier.
              The old name is kept as the product's <strong>brand</strong>, and{' '}
              <strong>{mergeFrom?.name || ''}</strong> is then deleted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              Use this when a "supplier" is actually a brand (e.g. <em>Dr Hauschka</em>) and should sit under a real distributor (e.g. <em>Ceres</em>).
            </div>

            <div className="space-y-2">
              <Label htmlFor="merge-target">Merge into supplier</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger id="merge-target" data-testid="merge-target-select">
                  <SelectValue placeholder="Pick a supplier..." />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {suppliers
                    .filter(s => s.id !== mergeFrom?.id)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)} disabled={mergeLoading}>Cancel</Button>
            <Button
              onClick={handleMerge}
              disabled={mergeLoading || !mergeTargetId}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="merge-supplier-confirm"
            >
              {mergeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wholesale Pricelist Dialog — generate B2B PDFs (internal + buyer copy) */}
      <Dialog open={wholesaleOpen} onOpenChange={setWholesaleOpen}>
        <DialogContent className="max-w-xl" data-testid="wholesale-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading uppercase">
              <FileText className="w-5 h-5 text-emerald-600" />
              Wholesale Pricelist
            </DialogTitle>
            <DialogDescription>
              Generate a pricelist for <span className="font-semibold">{wholesaleSupplier?.name}</span> to send to <span className="font-semibold">{wholesaleBuyer}</span>.
              Two copies will be available — internal (with your cost &amp; margin) and a clean buyer copy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Buyer name</Label>
                <Input value={wholesaleBuyer} onChange={(e) => setWholesaleBuyer(e.target.value)} placeholder="Health 2000" data-testid="wholesale-buyer-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Your business name</Label>
                <Input value={wholesaleSeller} onChange={(e) => setWholesaleSeller(e.target.value)} placeholder="The Wellness Store" data-testid="wholesale-seller-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Your margin on cost (%)</Label>
                <Input
                  type="number" min={0} max={500} step={1}
                  value={wholesaleMarkup}
                  onChange={(e) => setWholesaleMarkup(parseFloat(e.target.value) || 0)}
                  className="font-mono"
                  data-testid="wholesale-markup-input"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">How much YOU make per unit on top of cost.</p>
              </div>
              <div>
                <Label className="text-[11px] uppercase text-slate-500">{wholesaleBuyer}'s markup (%)</Label>
                <Input
                  type="number" min={0} max={500} step={1}
                  value={wholesaleBuyerMarkup}
                  onChange={(e) => setWholesaleBuyerMarkup(parseFloat(e.target.value) || 0)}
                  className="font-mono"
                  data-testid="wholesale-buyer-markup-input"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">Their typical retail markup (usually ~40%).</p>
              </div>
            </div>

            {/* Worked-example block — shows the full chain so the user can sanity-check the maths */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1 font-mono">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-sans font-semibold">Example on a $20 cost item</p>
              <p>You pay supplier <span className="float-right font-bold">$20.00</span></p>
              <p>+ Your margin {wholesaleMarkup}% <span className="float-right text-emerald-700">+${(20 * (wholesaleMarkup / 100)).toFixed(2)}</span></p>
              <p>= You sell to {wholesaleBuyer} at <span className="float-right font-bold text-emerald-800">${(20 * (1 + wholesaleMarkup / 100)).toFixed(2)}</span></p>
              <p>+ Their markup {wholesaleBuyerMarkup}% <span className="float-right text-slate-600">+${(20 * (1 + wholesaleMarkup / 100) * (wholesaleBuyerMarkup / 100)).toFixed(2)}</span></p>
              <p>= Their suggested retail <span className="float-right font-bold text-slate-800">${(20 * (1 + wholesaleMarkup / 100) * (1 + wholesaleBuyerMarkup / 100)).toFixed(2)}</span></p>
              <p className="text-[10px] text-slate-500 font-sans pt-1 border-t border-emerald-200 mt-1">
                💡 If their retail comes out HIGHER than your own shop price, you'd be undercutting yourself. The Internal copy flags any product where this happens in red.
              </p>
            </div>

            <div>
              <Label className="text-[11px] uppercase text-slate-500">Format</Label>
              <div className="flex gap-2 mt-1">
                {['pdf', 'csv'].map(f => (
                  <button key={f} type="button" onClick={() => setWholesaleFormat(f)}
                    className={`px-3 py-1.5 rounded border text-xs font-semibold uppercase ${wholesaleFormat === f ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`wholesale-format-${f}`}
                  >{f}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setWholesaleOpen(false)} disabled={generatingWholesale}>Cancel</Button>
            <Button
              variant="outline"
              onClick={() => downloadWholesalePricelist('internal')}
              disabled={generatingWholesale}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="download-internal-btn"
            >
              {generatingWholesale ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Internal copy (with cost &amp; margin)
            </Button>
            <Button
              onClick={() => downloadWholesalePricelist('buyer')}
              disabled={generatingWholesale}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="download-buyer-btn"
            >
              {generatingWholesale ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Buyer copy → {wholesaleBuyer}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
